import React, { createContext, useContext, useMemo } from 'react';
import { AuthContext } from './AuthContext';
import { MODULE_CODES, ADMIN_ONLY_MODULES, ModuleCode } from '../constants/modules';

type DocumentPermission = 'read' | 'write' | 'delete';

interface PermissionContextType {
  allowedModules: string[];
  hasAccess: (moduleCode: string) => boolean;
  hasWrite: (moduleCode: string) => boolean;
  hasDelete: (moduleCode: string) => boolean;
  hasDocumentAccess: (moduleCode: string, docCode: string, permission: DocumentPermission) => boolean;
  hasNavigate: (moduleCode: string) => boolean;
  hasDocAccess: (docCode: string) => boolean;
  hasDocNavigate: (docCode: string) => boolean;
  isAdmin: boolean;
  userSectors: string[];
  departmentPermissions: { added: string[]; write: string[]; delete: string[]; navigate: string[] } | null;
}

export const PermissionContext = createContext<PermissionContextType>({
  allowedModules: [],
  hasAccess: () => false,
  hasWrite: () => false,
  hasDelete: () => false,
  hasDocumentAccess: () => false,
  hasNavigate: () => false,
  hasDocAccess: () => true,
  hasDocNavigate: () => true,
  isAdmin: false,
  userSectors: [],
  departmentPermissions: null,
});

interface PermissionProviderProps {
  children: React.ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user } = useContext(AuthContext);

  const isAdmin = user?.role === 'admin';
  const userSectors: string[] = user?.allowedSectors || [];

  const allowedModules = useMemo(() => {
    if (!user) return [];

    // Admin gets all modules
    if (user.role === 'admin') {
      return Object.values(MODULE_CODES);
    }

    // Use allowedModules from login response (computed by server)
    if (user.allowedModules) {
      return user.allowedModules;
    }

    // Fallback: compute from overrides if allowedModules not provided
    const added = user.moduleOverrides?.added || [];
    const removed = user.moduleOverrides?.removed || [];

    const effectiveModules = added.filter((m: string) => !removed.includes(m));

    // Filter out admin-only modules for non-admins
    return effectiveModules.filter((m: string) => !ADMIN_ONLY_MODULES.includes(m as ModuleCode));
  }, [user]);

  const hasAccess = (moduleCode: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return allowedModules.includes(moduleCode);
  };

  const allowedWriteModules = useMemo(() => {
    if (!user) return [];
    if (user.role === 'admin') return Object.values(MODULE_CODES);
    return (user.allowedWriteModules || []) as string[];
  }, [user]);

  const allowedDeleteModules = useMemo(() => {
    if (!user) return [];
    if (user.role === 'admin') return Object.values(MODULE_CODES);
    return (user.allowedDeleteModules || []) as string[];
  }, [user]);

  const hasWrite = (moduleCode: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return allowedWriteModules.includes(moduleCode);
  };

  const hasDelete = (moduleCode: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return allowedDeleteModules.includes(moduleCode);
  };

  const hasDocumentAccess = (moduleCode: string, docCode: string, permission: DocumentPermission): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (permission === 'read' && hasAccess(moduleCode)) return true;
    if (permission === 'write' && hasWrite(moduleCode)) return true;
    if (permission === 'delete' && hasDelete(moduleCode)) return true;
    const key = String(docCode);
    if (user.documentTypeOverrides?.[moduleCode]?.[key]?.[permission] === true) return true;
    const legacyList = user.documentOverrides?.[permission === 'read' ? 'added' : permission];
    return Array.isArray(legacyList) && legacyList.includes(key);
  };

  const hasNavigate = (moduleCode: string): boolean => hasAccess(moduleCode);

  // Document-level permissions
  // documentOverrides.added is treated as an ADDITIVE allowlist, not a global restriction.
  // Module-level access is the real gate — every sidebar already calls hasAccess(moduleCode)
  // before filtering docs, so returning true here is safe (module gate applied upstream;
  // server enforces 403 as the hard backstop).  Per-doc entries in documentOverrides.added
  // only add extra doc-level access; they must not hide docs of modules the user holds.
  const hasDocAccess = (docCode: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return true;
  };

  const hasDocNavigate = (docCode: string): boolean => hasDocAccess(docCode);

  const departmentPermissions = user?.departmentPermissions
    ? {
        added: user.departmentPermissions.added || [],
        write: user.departmentPermissions.write || [],
        delete: user.departmentPermissions.delete || [],
        navigate: user.departmentPermissions.navigate || [],
      }
    : null;

  return (
    <PermissionContext.Provider value={{ allowedModules, hasAccess, hasWrite, hasDelete, hasDocumentAccess, hasNavigate, hasDocAccess, hasDocNavigate, isAdmin, userSectors, departmentPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook for easier access
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
